/**
 * Document Parser Client
 * Handles document parsing operations via Gateway API
 *
 * ⚠️ SECURITY: ALL parsing requests MUST be authorized.
 * See /SECURITY_GUIDELINES.md for mandatory security requirements.
 */

import { GATEWAY, validateGatewayUrl } from '../app-constants';
import { logger } from '../logger';
import type {
  ApiResponse,
  AuthService,
  ParseOptions,
  TaskIdResponse,
} from './types';
import {
  getRequiredCsrfToken,
  withCsrfRetry,
  assertAuthorized,
  buildProxyHeaders,
  buildDirectHeaders,
} from '../auth-utils';
import { pendingIfSessionEnded, suspendIfSessionEnded } from '../session-ended';

export interface DocumentParserClientOptions {
  useProxy: boolean;
  proxyUrl: string | null;
  baseUrl: string;
  endpoint: string;
  authService: AuthService | null;
  authToken: string | null;
}

export class DocumentParserClient {
  private options: DocumentParserClientOptions;

  constructor(options: DocumentParserClientOptions) {
    this.options = options;
  }

  /**
   * Update authentication token
   */
  setAuthToken(token: string | null): void {
    this.options.authToken = token;
  }

  /** Wire keys that are docling options (everything on ParseOptions except transport keys). */
  private static readonly DOCLING_OPTION_KEYS: Array<keyof ParseOptions> = [
    'do_formula_enrichment',
    'do_ocr',
    'force_ocr',
    'ocr_lang',
    'do_table_structure',
    'table_mode',
    'page_range',
  ];

  /**
   * Build FormData for document parsing request
   */
  private buildParseFormData(
    arrayBuffer: ArrayBuffer,
    filename: string,
    mimeType: string,
    options: ParseOptions
  ): FormData {
    const formData = new FormData();
    const blob = new Blob([arrayBuffer], { type: mimeType });
    const toFormats = options.to_formats || ['json', 'html'];

    formData.append('command', 'documents.parse');
    formData.append('language', options.language || 'eng');
    formData.append('files', blob, filename);

    toFormats.forEach((format) => {
      formData.append('to_formats[]', format);
    });

    this.appendParseOptions(formData, options);

    return formData;
  }

  /**
   * Encode docling-serve options as a single JSON `options` field.
   * Only present (defined) option keys are included; when none are present
   * the field is omitted entirely for backward compatibility.
   */
  private appendParseOptions(formData: FormData, options: ParseOptions): void {
    const doclingOptions: Record<string, unknown> = {};
    for (const key of DocumentParserClient.DOCLING_OPTION_KEYS) {
      const value = options[key];
      if (value !== undefined) {
        doclingOptions[key] = value;
      }
    }
    if (Object.keys(doclingOptions).length > 0) {
      formData.append('options', JSON.stringify(doclingOptions));
    }
  }

  /**
   * Get request configuration (URL and headers) based on proxy mode
   */
  private getParseRequestConfig(): {
    url: string;
    headers: Record<string, string>;
    fetchOptions: { credentials?: RequestCredentials; mode?: RequestMode };
  } {
    if (this.options.useProxy && this.options.proxyUrl) {
      // Get CSRF token from auth service
      const csrfToken = getRequiredCsrfToken(this.options.authService);

      return {
        url: this.options.proxyUrl,
        headers: buildProxyHeaders(csrfToken, false),
        fetchOptions: {
          credentials: 'include', // Send session cookie (gitterdan_session)
        },
      };
    }

    // Legacy direct Gateway access
    if (!this.options.authToken) {
      throw new Error(
        'Gateway service not initialized. Call initialize() first.'
      );
    }

    return {
      url: `${this.options.baseUrl}${this.options.endpoint}`,
      headers: buildDirectHeaders(this.options.authToken, false),
      fetchOptions: {
        mode: 'cors',
        credentials: 'omit',
      },
    };
  }

  /**
   * Submit parse request to Gateway
   */
  private async submitParseRequest(
    url: string,
    headers: Record<string, string>,
    formData: FormData,
    fetchOptions: { credentials?: RequestCredentials; mode?: RequestMode }
  ): Promise<string> {
    const pending = pendingIfSessionEnded<string>();
    if (pending) return pending;

    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort(),
      GATEWAY.PARSE_TIMEOUT_MS
    );

    try {
      // SECURITY: Validate URL against allowlist to prevent SSRF
      validateGatewayUrl(url);

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
        signal: controller.signal,
        ...fetchOptions,
      });

      clearTimeout(timeoutId);

      logger.debug(
        `Gateway response status: ${response.status} ${response.statusText}`
      );

      // Handle common auth errors
      await assertAuthorized(response);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Gateway parse request failed: ${response.status} ${response.statusText} - ${errorText}`
        );
      }

      const result: ApiResponse<TaskIdResponse> = await response.json();
      logger.debug('Gateway parse response:', result);

      if (result.status === 'ok' && result.data?.task_id) {
        return result.data.task_id;
      }

      throw new Error('Invalid response from Gateway: missing task_id');
    } catch (error) {
      clearTimeout(timeoutId);
      const suspended = suspendIfSessionEnded<string>(error);
      if (suspended) return suspended;
      throw this.handleParseError(error);
    }
  }

  /**
   * Handle parse request errors
   */
  private handleParseError(error: unknown): Error {
    logger.error('Gateway parse documents error:', error);

    if ((error as Error).name === 'AbortError') {
      return new Error(
        'Gateway parse request timed out. The document may be too large or complex. Please try a smaller file or contact support.'
      );
    }

    if (
      (error as Error).message.includes('fetch') ||
      (error as Error).message.includes('network')
    ) {
      return new Error(
        `Network error while submitting document to Gateway. Please check your internet connection and try again. Details: ${(error as Error).message}`
      );
    }

    return error as Error;
  }

  /**
   * Parse documents using the Gateway API (via proxy or direct)
   * @param arrayBuffer - Document file as ArrayBuffer
   * @param filename - Original filename
   * @param mimeType - MIME type of the document
   * @param options - Parsing options (to_formats, etc.)
   * @returns Task ID for the parsing job
   * @throws If request fails or service not initialized
   */
  async parseDocuments(
    arrayBuffer: ArrayBuffer,
    filename: string,
    mimeType: string,
    options: ParseOptions = {}
  ): Promise<string> {
    // After a session loss: no CSRF lookup, no request, no error.
    const pending = pendingIfSessionEnded<string>();
    if (pending) return pending;

    const isProxy = this.options.useProxy && this.options.proxyUrl;
    const mode = isProxy ? 'proxy' : 'direct';

    logger.info(`Submitting document to Gateway via ${mode} for parsing...`);
    logger.debug(
      `File: ${filename}, MIME type: ${mimeType}, Size: ${arrayBuffer.byteLength} bytes`
    );

    const formData = this.buildParseFormData(
      arrayBuffer,
      filename,
      mimeType,
      options
    );
    const toFormats = options.to_formats || ['json', 'html'];

    // The config (and so the CSRF token) is built per attempt: a rotated token
    // is refreshed and the upload retried once (#113).
    return withCsrfRetry(this.options.authService, () => {
      const { url, headers, fetchOptions } = this.getParseRequestConfig();
      logger.debug(
        `Submitting to ${url} with formats: ${toFormats.join(', ')}`
      );
      return this.submitParseRequest(url, headers, formData, fetchOptions);
    });
  }
}
